import type { FC, ReactNode } from 'react'
import type { Currency } from '@/types'

import { cn } from '@/lib/utils'
import { currencies } from '@/constants/site'

interface CurrencyPickerProps {
    name: string
    value: string
    onChange: (currency: Currency) => void
    tone?: 'light' | 'dark'
    invalid?: boolean
    describedBy?: string
    idPrefix?: string
}

export const CurrencyPicker: FC<CurrencyPickerProps> = ({
    name,
    value,
    onChange,
    tone = 'light',
    invalid = false,
    describedBy,
    idPrefix = name
}): ReactNode => {
    const isDark = tone === 'dark'

    return (
        <div className='grid gap-3 sm:grid-cols-3'>
            {currencies.map(currency => {
                const checked = value === currency.code

                return (
                    <label
                        key={currency.code}
                        htmlFor={`${idPrefix}-${currency.code}`}
                        className={cn(
                            'group relative flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3.5 transition-all duration-300 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand-orange has-[:focus-visible]:ring-offset-2',
                            isDark ? 'has-[:focus-visible]:ring-offset-brand-charcoal' : 'has-[:focus-visible]:ring-offset-brand-white',
                            checked
                                ? 'border-brand-orange bg-brand-orange/12 shadow-[0_12px_30px_-18px_rgba(232,121,50,0.9)]'
                                : isDark
                                    ? 'border-white/15 hover:border-white/35'
                                    : invalid
                                        ? 'border-destructive bg-white'
                                        : 'border-input bg-white hover:border-brand-stone/40'
                        )}
                    >
                        <input
                            id={`${idPrefix}-${currency.code}`}
                            type='radio'
                            name={name}
                            value={currency.code}
                            checked={checked}
                            onChange={() => onChange(currency.code)}
                            aria-invalid={invalid ? true : undefined}
                            aria-describedby={describedBy}
                            className='sr-only'
                        />

                        <span
                            aria-hidden='true'
                            className={cn(
                                'inline-flex size-10 shrink-0 items-center justify-center rounded-full font-heading text-xl font-semibold transition-colors',
                                checked
                                    ? 'bg-brand-orange text-brand-charcoal'
                                    : isDark ? 'bg-white/10 text-brand-gold' : 'bg-brand-cream text-brand-ember'
                            )}
                        >
                            {currency.symbol}
                        </span>

                        <span className='flex flex-col'>
                            <span className={cn('text-sm font-semibold', isDark ? 'text-brand-white' : 'text-brand-charcoal')}>
                                {currency.code}
                            </span>

                            <span className={cn('text-xs', isDark ? 'text-brand-mist' : 'text-brand-stone')}>
                                {currency.label}
                            </span>
                        </span>
                    </label>
                )
            })}
        </div>
    )
}
